import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  startWith,
} from 'rxjs';
import { Injectable } from '@angular/core';
import { isEqual } from 'lodash';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CpuMonitoringService, PerformanceTier } from './cpu-monitoring.service';
import { HeapMonitoringService, HeapStatus } from './heap-monitoring.service';
import { FLAGS, FlagsService } from './flags.service';

export enum DeviceResourceStatus {
  INSUFFICIENT = 'INSUFFICIENT',
  GOOD = 'GOOD',
}
export interface DeviceResources {
  status: DeviceResourceStatus;
  resourcesStatus: { cpu: PerformanceTier; heap: HeapStatus };
}
@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DeviceResourcesMonitoringService {
  private deviceResourcesBehaviorSubject = new BehaviorSubject<DeviceResources>({
    status: DeviceResourceStatus.GOOD,
    resourcesStatus: { cpu: PerformanceTier.tierOne, heap: HeapStatus.GOOD },
  });

  private deviceResourcesObservable$ = this.deviceResourcesBehaviorSubject.asObservable();

  get deviceResources$(): Observable<DeviceResources> {
    return this.deviceResourcesObservable$;
  }

  get deviceResources(): DeviceResources {
    return this.deviceResourcesBehaviorSubject.getValue();
  }

  constructor(
    private cpuMonitoringService: CpuMonitoringService,
    private heapMonitoringService: HeapMonitoringService,
    private flagsService: FlagsService,
  ) {
    combineLatest([
      this.flagsService
        .featureFlagChanged(FLAGS.PERFORMANCE_INTERVENTION_CPU)
        .pipe(untilDestroyed(this), distinctUntilChanged()),
      this.flagsService
        .featureFlagChanged(FLAGS.PERFORMANCE_INTERVENTION_MEMORY)
        .pipe(untilDestroyed(this), distinctUntilChanged()),
      this.heapMonitoringService.heapStatus$,
      this.cpuMonitoringService.currentTier$,
    ])
      .pipe(
        untilDestroyed(this),
        map(
          ([
            performanceInterventionCPU,
            performanceInterventionMemory,
            heapStatus,
            currentCpuTier,
          ]): DeviceResources => {
            let resourcesSufficient = true;

            if (performanceInterventionCPU && currentCpuTier !== PerformanceTier.tierOne) {
              resourcesSufficient = false;
            }

            const includeHeapInCalculations =
              performanceInterventionMemory &&
              (this.flagsService.featureFlagsVariables[FLAGS.PERFORMANCE_INTERVENTION_MEMORY]
                ?.show_low_memory_indicator as boolean);

            if (includeHeapInCalculations && heapStatus === HeapStatus.INSUFFICIENT) {
              resourcesSufficient = false;
            }

            return {
              status: resourcesSufficient
                ? DeviceResourceStatus.GOOD
                : DeviceResourceStatus.INSUFFICIENT,
              resourcesStatus: { cpu: currentCpuTier, heap: heapStatus },
            };
          },
        ),
        distinctUntilChanged(isEqual),
      )
      .subscribe(this.deviceResourcesBehaviorSubject);
  }

  public isDeviceStruggling$ = this.deviceResources$.pipe(
    map(this.isDeviceStruggling),
    distinctUntilChanged(),
    startWith(false),
  );

  public isDeviceStruggling(devicesResources: DeviceResources): boolean {
    const isMemoryInsufficient =
      devicesResources.status === DeviceResourceStatus.INSUFFICIENT &&
      devicesResources.resourcesStatus.cpu === PerformanceTier.tierOne;

    const isComputerTierThree = devicesResources.resourcesStatus.cpu === PerformanceTier.tierThree;

    return isMemoryInsufficient || isComputerTierThree;
  }
}
